import { useQuery } from '@tanstack/react-query';
import { fetchSnapshot } from '../lib/api.js';

// Refresh alinhado com o ciclo do poller (~60s) + cache server-side (25s).
// 30s dá ~2 hits por ciclo do poller, suficiente para o NOC ver mudanças
// dentro de 1 minuto.
const REFETCH_MS = 30_000;

/**
 * Hook de snapshot completo do Mapa v3.
 *
 * Antes (v2): cada filtro do user disparava 3 requests separados
 * (`/onus`, `/onus/facets`, `/clusters`). Latência cumulativa alta porque
 * cada request faz seu próprio COUNT + JOIN no mart.
 *
 * Agora (v3): 1 request por status retorna TUDO (rows + facets), e os
 * filtros (search/olt/aging/sort/page) são aplicados client-side em
 * memória sobre o array de rows. Sem latência percebida ao mexer no
 * filtro — só refetch a cada 30s.
 *
 * Limites práticos:
 *   - status='bad': ~300 rows ~75KB
 *   - status='online'/'all': ~9k rows ~2MB (gzip ~400KB) — aceitável
 *
 * Retorna:
 *   { rows, facets, generatedAt, source, snapshotCache, isLoading, isFetching, error }
 */
export function useSnapshot({ status = 'bad', enabled = true } = {}) {
  const q = useQuery({
    queryKey: ['v2', 'snapshot', { status }],
    queryFn: () => fetchSnapshot({ status }),
    enabled,
    refetchInterval: REFETCH_MS,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });

  const payload = q.data?.data || {};
  return {
    rows: payload.rows || [],
    facets: payload.facets || {},
    generatedAt: payload.generated_at || null,
    source: q.data?.source || null,
    snapshotCache: null, // X-Snapshot-Cache header não chega via fetch wrapper atual
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error,
  };
}
